/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import is_function from '../is/is_function';
import is_iterable from '../is/is_iterable';
import { TGenerator, TIterable } from '../types';

function* noop() {
  return undefined;
}

export interface Iterable {
  /**
   * unwind an iterable so it can be iterated over
   *
   * @public
   * @function f1.iterable
   * @param iterable an iterable iterator which unwinds to become an iterator
   * @example
   *
   * Arrays, Sets and Maps are iterators
   *
   * ```ts
   * const iterator = f1.iterable([1, 2, 3]);
   * for (let v of iterator) {
   *   // do something with the iterator
   * }
   * ```
   *
   * We can accept generators and iterable iterators.
   *
   * ```ts
   * const iterator = f1.iterable(function *() {
   *   yield 1;
   *   yield 2;
   * });
   * for (let v of iterator) {
   *   // do something with the iterator
   * }
   * ```
   *
   */
  <V>(iterable: TGenerator<V> | TIterable<V> | IterableIterator<V>): IterableIterator<V>;
}

/**
 * @see Iterable
 */
export default <V>(iterable: TGenerator<V> | TIterable<V> | IterableIterator<V>): IterableIterator<V> => {
  return is_function(iterable)
    ? (iterable() as IterableIterator<V>)
    : is_iterable(iterable)
    ? iterable.constructor
      ? (iterable as IterableIterator<V>)
      : (iterable[Symbol.iterator]() as IterableIterator<V>)
    : (noop() as IterableIterator<V>);
};
